'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { Share2, BookOpen } from 'lucide-react';
import { LogoPhi } from '@/components/brand/logo-phi';
import { cn } from '@/lib/utils';

export interface CardRevealCard {
  slug: string;
  nameEs: string;
  arcana: 'mayor' | 'menor' | 'custom';
  imagePublicId: string;
  interpretationShortEs: string;
  interpretationLongEs: string;
  keywords: string[];
}

interface CardRevealProps {
  card: CardRevealCard;
  drawDate: string;
  /** ms hasta la próxima carta disponible (siguiente día MX). */
  msUntilNext: number;
  /** Si el usuario ya había revelado antes — mostramos la carta abierta, no el reverso. */
  alreadyRevealed: boolean;
}

const CLOUDINARY_CLOUD = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME;

function resolveImageSrc(publicId: string): string {
  if (publicId.startsWith('http://') || publicId.startsWith('https://')) {
    return publicId;
  }
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/f_auto,q_auto,w_600/${publicId}`;
}

export function CardReveal({ card, drawDate, msUntilNext, alreadyRevealed }: CardRevealProps) {
  const reduced = useReducedMotion();
  const [revealed, setRevealed] = useState(alreadyRevealed);
  const [remaining, setRemaining] = useState(msUntilNext);

  useEffect(() => {
    const t = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1000));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  function handleReveal() {
    if (revealed) return;
    setRevealed(true);
  }

  async function handleShare() {
    const shareData = {
      title: `Mi carta de hoy: ${card.nameEs} · NQORO`,
      text: card.interpretationShortEs,
      url: typeof window !== 'undefined' ? window.location.href : 'https://nqoro.com/tarot',
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // usuario canceló
      }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${shareData.text}\n\n${shareData.url}`);
    }
  }

  const formattedDate = new Date(drawDate + 'T12:00:00').toLocaleDateString('es-MX', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'America/Mexico_City',
  });

  return (
    <div className="flex flex-col items-center text-center">
      <p className="type-eyebrow text-[var(--color-fg-subtle)]">
        Tu carta de hoy · {formattedDate}
      </p>

      <div className="mt-8 mb-10 w-full max-w-[380px]">
        {revealed ? (
          <CardFront card={card} reduced={!!reduced} />
        ) : (
          <CardBack onClick={handleReveal} reduced={!!reduced} />
        )}
      </div>

      <AnimatePresence>
        {revealed ? (
          <motion.div
            key="content"
            initial={reduced ? {} : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduced ? 0 : 0.6, delay: reduced ? 0 : 0.4 }}
            className="mx-auto w-full max-w-xl"
          >
            <h1 className="type-h1 font-serif text-[var(--color-fg)]">{card.nameEs}</h1>
            <p className="type-body-lg mt-3 text-[var(--color-fg-muted)] italic">
              {card.interpretationShortEs}
            </p>
            {card.keywords.length > 0 ? (
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                {card.keywords.map((k) => (
                  <span
                    key={k}
                    className="type-caption rounded-xs border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-fg-muted)]"
                  >
                    {k}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="type-body mt-10 space-y-4 text-left leading-relaxed text-[var(--color-fg-muted)]">
              <p>{card.interpretationLongEs}</p>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={handleShare}
                className="type-nav inline-flex h-11 items-center gap-2 rounded-sm border border-[var(--color-border-strong)] px-5 text-[var(--color-fg-muted)] transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
              >
                <Share2 className="h-4 w-4" />
                Compartir
              </button>
              <Link
                href="/cuenta/tarot"
                className="type-nav inline-flex h-11 items-center gap-2 rounded-sm bg-[var(--color-oro-viejo)] px-5 text-[var(--color-noir)] hover:bg-[var(--color-oro-bright)]"
              >
                <BookOpen className="h-4 w-4" />
                Ver historial
              </Link>
            </div>

            <Countdown remainingMs={remaining} />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function CardBack({ onClick, reduced }: { onClick: () => void; reduced: boolean }) {
  const motionProps = reduced
    ? {}
    : {
        whileHover: { scale: 1.02 },
        whileTap: { scale: 0.98 },
        animate: { y: [0, -6, 0] },
        transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' as const },
      };
  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-label="Voltear la carta"
      {...motionProps}
      className="group relative mx-auto aspect-[5/8] w-full overflow-hidden rounded-sm border border-[var(--color-oro-viejo)] bg-gradient-to-br from-[#141413] via-[#1a1816] to-[#0B0B0A] shadow-[0_20px_60px_rgba(201,166,107,0.15)] transition-shadow hover:shadow-[0_28px_80px_rgba(201,166,107,0.25)]"
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <LogoPhi size={64} tone="gold" />
          <p className="type-eyebrow text-[var(--color-oro-viejo)] opacity-80">
            Φ · Toca para revelar
          </p>
        </div>
      </div>
      <div
        aria-hidden
        className="absolute inset-4 rounded-sm border border-[var(--color-oro-viejo)]/30"
      />
    </motion.button>
  );
}

function CardFront({ card, reduced }: { card: CardRevealCard; reduced: boolean }) {
  const src = resolveImageSrc(card.imagePublicId);
  const flipStyle: CSSProperties = reduced ? {} : ({ perspective: '1200px' } as CSSProperties);

  return (
    <div style={flipStyle} className="mx-auto aspect-[5/8] w-full">
      <motion.div
        initial={reduced ? { opacity: 0 } : { rotateY: -180, opacity: 0 }}
        animate={reduced ? { opacity: 1 } : { rotateY: 0, opacity: 1 }}
        transition={{ duration: reduced ? 0.5 : 0.9, ease: 'easeInOut' }}
        style={reduced ? {} : { transformStyle: 'preserve-3d' as const }}
        className={cn(
          'relative h-full w-full overflow-hidden rounded-sm border shadow-[0_20px_60px_rgba(201,166,107,0.15)]',
          card.arcana === 'mayor'
            ? 'border-[var(--color-oro-viejo)]'
            : 'border-[var(--color-border-strong)]'
        )}
      >
        <Image
          src={src}
          alt={card.nameEs}
          fill
          sizes="(max-width: 640px) 100vw, 380px"
          className="object-cover"
          priority
          unoptimized
        />
      </motion.div>
    </div>
  );
}

function Countdown({ remainingMs }: { remainingMs: number }) {
  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return (
    <div className="mt-12 border-t border-[var(--color-border)] pt-8">
      <p className="type-eyebrow text-[var(--color-fg-subtle)]">Próxima carta</p>
      <p className="type-h3 mt-2 font-mono text-[var(--color-fg)] tabular-nums">
        {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:
        {String(seconds).padStart(2, '0')}
      </p>
      <p className="type-caption mt-1 text-[var(--color-fg-subtle)]">
        Mañana a las 00:00 hora CDMX
      </p>
    </div>
  );
}
